import * as React from "react";
import {
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";

import { cn } from "@/lib/utils";

export type ChartConfig = Record<
  string,
  {
    label?: string;
    color?: string;
  }
>;

type ChartContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  config?: ChartConfig;
  children: React.ReactElement;
};

const ChartContainer = React.forwardRef<HTMLDivElement, ChartContainerProps>(
  ({ className, config, children, style, ...props }, ref) => {
    const configStyles = config
      ? Object.fromEntries(
          Object.entries(config)
            .filter(([, value]) => value?.color)
            .map(([key, value]) => [`--color-${key}`, value?.color as string])
        )
      : {};

    return (
      <div
        ref={ref}
        className={cn("h-[250px] w-full", className)}
        style={{ ...style, ...(configStyles as React.CSSProperties) }}
        {...props}
      >
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    );
  }
);
ChartContainer.displayName = "ChartContainer";

const ChartTooltip = RechartsTooltip;

type ChartTooltipContentProps = {
  active?: boolean;
  payload?: Array<{ name?: string; dataKey?: string; value?: number }>;
  label?: string;
  hideLabel?: boolean;
};

const ChartTooltipContent = React.forwardRef<HTMLDivElement, ChartTooltipContentProps>(
  ({ active, payload, label, hideLabel }, ref) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div ref={ref} className="rounded-md border bg-background px-3 py-2 text-xs shadow-md">
        {!hideLabel && label != null ? (
          <div className="font-medium">{label}</div>
        ) : null}
        <div className="mt-1 space-y-1">
          {payload.map((item: { name?: string; dataKey?: string; value?: number }, index: number) => (
            <div key={index} className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">
                {item.name ?? item.dataKey}
              </span>
              <span className="font-medium">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
);
ChartTooltipContent.displayName = "ChartTooltipContent";

export { ChartContainer, ChartTooltip, ChartTooltipContent };
